import {useEffect, useMemo, useState} from 'react';
import type {CSSProperties, ChangeEvent} from 'react';
import type {FilterPriceSliderProps} from './filter-price-slider.types';

interface UseFilterPriceSliderReturn {
    localMin: number;
    localMax: number;
    activeTrackStyle: CSSProperties;
    handleMinChange: (e: ChangeEvent<HTMLInputElement>) => void;
    handleMaxChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

export function useFilterPriceSlider({
    min,
    max,
    value,
    onChange,
}: Pick<FilterPriceSliderProps, 'min' | 'max' | 'value' | 'onChange'>): UseFilterPriceSliderReturn {
    const normalizedValue = useMemo<[number, number]>(() => {
        if (Array.isArray(value)) {
            const start = Number.isFinite(value[0]) ? value[0] : min;
            const end = Number.isFinite(value[1]) ? value[1] : max;

            return [Math.max(min, Math.min(start, max)), Math.max(min, Math.min(end, max))];
        }

        const singleValue = Number.isFinite(value) ? value : min;
        return [min, Math.max(min, Math.min(singleValue, max))];
    }, [value, min, max]);

    const [localMin, setLocalMin] = useState<number>(normalizedValue[0]);
    const [localMax, setLocalMax] = useState<number>(normalizedValue[1]);

    useEffect(() => {
        setLocalMin(normalizedValue[0]);
        setLocalMax(normalizedValue[1]);
    }, [normalizedValue]);

    const emitChange = (nextMin: number, nextMax: number) => {
        onChange?.([nextMin, nextMax]);
    };

    const handleMinChange = (e: ChangeEvent<HTMLInputElement>) => {
        const next = Math.min(Number(e.target.value), localMax);
        setLocalMin(next);
        emitChange(next, localMax);
    };

    const handleMaxChange = (e: ChangeEvent<HTMLInputElement>) => {
        const next = Math.max(Number(e.target.value), localMin);
        setLocalMax(next);
        emitChange(localMin, next);
    };

    const activeTrackStyle = useMemo<CSSProperties>(() => {
        const range = Math.max(max - min, 1);
        const leftPercent = ((localMin - min) / range) * 100;
        const rightPercent = ((localMax - min) / range) * 100;

        return {
            left: `${leftPercent}%`,
            width: `${Math.max(rightPercent - leftPercent, 0)}%`,
        };
    }, [localMin, localMax, min, max]);

    return {
        localMin,
        localMax,
        activeTrackStyle,
        handleMinChange,
        handleMaxChange,
    };
}
